import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useCurrency, CURRENCIES } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { cn } from "../lib/utils";
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  History,
  Edit3,
  RefreshCw,
  FileText,
  Loader2,
  User,
  ExternalLink,
  ThumbsUp,
  Banknote,
  Save,
} from "lucide-react";
import { PaymentSourceSelector } from "../components/PaymentSourceSelector";
import { toast } from "sonner";

const TaskPaymentDetailPage = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { formatAmount, getCurrencySymbol } = useCurrency();
  
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditLog, setAuditLog] = useState([]);
  
  // Edit mode for amount/currency
  const [isEditing, setIsEditing] = useState(false);
  const [editAmount, setEditAmount] = useState("");
  const [editCurrency, setEditCurrency] = useState("USD");
  const [saving, setSaving] = useState(false);
  
  // Edit paid details (date/source/comment)
  const [isEditingPaidDetails, setIsEditingPaidDetails] = useState(false);
  const [editPaidDate, setEditPaidDate] = useState("");
  const [editPaidSource, setEditPaidSource] = useState("");
  const [editPaidComment, setEditPaidComment] = useState("");
  const [savingPaidDetails, setSavingPaidDetails] = useState(false);
  
  // Status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [changingStatus, setChangingStatus] = useState(false);

  useEffect(() => {
    fetchPayment();
    fetchAuditLog();
  }, [paymentId]);

  const fetchAuditLog = async () => {
    try {
      const res = await api.get(`/audit-log?entity_type=task_payment&entity_id=${paymentId}`);
      setAuditLog(res.data || []);
    } catch (error) {
      console.error("Failed to load audit log:", error);
    }
  };

  const fetchPayment = async () => {
    try {
      setLoading(true);
      // Get all task payments and find by ID
      const res = await api.get("/task-payments");
      const found = (res.data || []).find(p => p.id === paymentId);
      if (found) {
        setPayment(found);
        setEditAmount(found.amount?.toString() || "0");
        setEditCurrency(found.currency || "USD");
      }
    } catch (error) {
      toast.error("Failed to load payment details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    setChangingStatus(true);
    try {
      // Use the existing API endpoint
      if (newStatus === "confirmed") {
        await api.put(`/task-payments/${paymentId}?status=confirmed`);
      } else if (newStatus === "paid") {
        await api.put(`/task-payments/${paymentId}?status=paid&paid_date=${paidDate}`);
      } else if (newStatus === "pending") {
        await api.put(`/task-payments/${paymentId}?status=pending`);
      }
      
      toast.success("Status changed successfully");
      setStatusDialogOpen(false);
      fetchPayment();
    } catch (error) {
      toast.error("Failed to change status");
    } finally {
      setChangingStatus(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/task-payments/${paymentId}/edit`, {
        amount: parseFloat(editAmount) || payment.amount,
        currency: editCurrency
      });
      toast.success("Payment updated");
      setIsEditing(false);
      fetchPayment();
      fetchAuditLog();
    } catch (error) {
      toast.error("Failed to update payment");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePaidDetails = async () => {
    setSavingPaidDetails(true);
    try {
      const body = {};
      if (editPaidDate && editPaidDate !== payment.paid_date) body.paid_date = editPaidDate;
      if (editPaidSource !== (payment.paid_source || "")) body.paid_source = editPaidSource;
      if (editPaidComment !== (payment.paid_comment || "")) body.paid_comment = editPaidComment;
      
      if (Object.keys(body).length === 0) {
        setIsEditingPaidDetails(false);
        return;
      }
      
      await api.put(`/task-payments/${paymentId}/edit`, body);
      toast.success("Payment details updated");
      setIsEditingPaidDetails(false);
      fetchPayment();
      fetchAuditLog();
    } catch (error) {
      toast.error("Failed to update");
    } finally {
      setSavingPaidDetails(false);
    }
  };

  const startEditPaidDetails = () => {
    setEditPaidDate(payment.paid_date || "");
    setEditPaidSource(payment.paid_source || "");
    setEditPaidComment(payment.paid_comment || "");
    setIsEditingPaidDetails(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Paid</Badge>;
      case "confirmed":
        return <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30">Approved</Badge>;
      default:
        return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Pending</Badge>;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <AlertCircle className="h-12 w-12 mx-auto mb-2" />
        <p>Payment not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="task-payment-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/payments")}
            className="text-zinc-400 hover:text-zinc-50 h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-zinc-50">{payment.task_name}</h1>
            <p className="text-xs text-zinc-500">
              Task Payment • {payment.project_name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(payment.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Main Info */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4 text-green-400" />
              Payment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Amount & Currency - Editable */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[10px] text-zinc-500 uppercase">Amount & Currency</Label>
                {!isEditing ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditing(true)}
                    className="h-6 px-2 text-zinc-400 hover:text-zinc-50"
                  >
                    <Edit3 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setIsEditing(false);
                        setEditAmount(payment.amount?.toString() || "0");
                        setEditCurrency(payment.currency || "USD");
                      }}
                      className="h-6 px-2 text-zinc-400 hover:text-zinc-50"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={saving}
                      className="h-6 px-2 bg-green-600 hover:bg-green-700"
                    >
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                      Save
                    </Button>
                  </div>
                )}
              </div>
              
              {!isEditing ? (
                <p className="text-lg font-bold text-green-400">
                  {formatAmount(payment.amount, payment.currency || "USD")}
                  <span className="text-xs text-zinc-500 ml-2 font-normal">({payment.currency || "USD"})</span>
                </p>
              ) : (
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-zinc-50 flex-1"
                    placeholder="Amount"
                  />
                  <Select value={editCurrency} onValueChange={setEditCurrency}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-50 w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {CURRENCIES.map(c => (
                        <SelectItem key={c} value={c} className="text-zinc-50">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Task Link */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-[10px] text-zinc-500 uppercase">Task</Label>
              <Link 
                to={`/tasks/${payment.task_id}`}
                className="text-sm text-orange-400 hover:text-orange-300 flex items-center gap-1 mt-1"
              >
                {payment.task_name}
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>

            {/* Artist */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-[10px] text-zinc-500 uppercase flex items-center gap-1">
                <User className="h-3 w-3" />
                Artist
              </Label>
              <p className="text-sm text-zinc-50 mt-1">{payment.artist_name || "—"}</p>
            </div>

            {/* Project */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-[10px] text-zinc-500 uppercase">Project</Label>
              <p className="text-sm text-zinc-50 mt-1">{payment.project_name || "—"}</p>
            </div>

            {/* Notes */}
            {payment.notes && (
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <Label className="text-[10px] text-zinc-500 uppercase flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Notes
                </Label>
                <p className="text-sm text-zinc-300 mt-1">{payment.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status & Actions */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <Clock className="h-4 w-4 text-purple-400" />
              Status & Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Current Status with Change button */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase">Current Status</p>
                  <div className="mt-1">{getStatusBadge(payment.status)}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewStatus(payment.status);
                    setPaidDate(payment.paid_date || new Date().toISOString().slice(0, 10));
                    setStatusDialogOpen(true);
                  }}
                  className="border-zinc-700 text-zinc-400 hover:text-zinc-50 h-8"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  Change
                </Button>
              </div>
            </div>

            {/* Quick Action Buttons */}
            {payment.status === "pending" && (
              <Button
                onClick={async () => {
                  setChangingStatus(true);
                  try {
                    await api.put(`/task-payments/${paymentId}?status=confirmed`);
                    toast.success("Payment approved successfully");
                    fetchPayment();
                  } catch (error) {
                    toast.error("Failed to approve payment");
                  } finally {
                    setChangingStatus(false);
                  }
                }}
                disabled={changingStatus}
                className="w-full bg-sky-600 hover:bg-sky-700"
              >
                {changingStatus ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ThumbsUp className="h-4 w-4 mr-2" />}
                Approve Payment
              </Button>
            )}
            
            {payment.status === "confirmed" && (
              <Button
                onClick={() => {
                  setNewStatus("paid");
                  setPaidDate(new Date().toISOString().slice(0, 10));
                  setStatusDialogOpen(true);
                }}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <Banknote className="h-4 w-4 mr-2" />
                Mark as Paid
              </Button>
            )}

            {/* Client Confirmed Date */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-[10px] text-zinc-500 uppercase">Client Confirmed Date</p>
              <p className="text-sm text-zinc-50 mt-1">{formatDateShort(payment.client_confirmed_date)}</p>
            </div>

            {/* Paid Date — editable for finance */}
            {payment.status === "paid" && (
              <div className="p-3 bg-green-900/20 border border-green-800/30 rounded-lg">
                {isEditingPaidDetails ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] text-green-400 uppercase block mb-1">Дата оплаты</label>
                      <Input
                        type="date"
                        value={editPaidDate}
                        onChange={(e) => setEditPaidDate(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
                        data-testid="edit-paid-date"
                      />
                    </div>
                    <PaymentSourceSelector value={editPaidSource} onChange={setEditPaidSource} />
                    <div>
                      <label className="text-[10px] text-zinc-400 uppercase block mb-1">Комментарий</label>
                      <Textarea
                        value={editPaidComment}
                        onChange={(e) => setEditPaidComment(e.target.value)}
                        placeholder="Примечание к платежу..."
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 h-14 resize-none text-sm"
                        data-testid="edit-paid-comment"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSavePaidDetails} disabled={savingPaidDetails} className="bg-green-600 hover:bg-green-700 h-7 text-xs" data-testid="save-paid-details-btn">
                        <Save className="h-3 w-3 mr-1" />
                        {savingPaidDetails ? "..." : "Save"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setIsEditingPaidDetails(false)} className="border-zinc-700 h-7 text-xs">
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-green-400 uppercase">Payment Date</p>
                      <Button size="icon" variant="ghost" className="h-5 w-5 text-zinc-500 hover:text-green-400" onClick={startEditPaidDetails} data-testid="edit-paid-details-btn">
                        <Edit3 className="h-3 w-3" />
                      </Button>
                    </div>
                    <p className="text-sm text-green-300 font-medium mt-1">{formatDateShort(payment.paid_date)}</p>
                    {payment.paid_source && (
                      <p className="text-xs text-green-500 mt-1">Источник: {payment.paid_source}</p>
                    )}
                    {payment.paid_comment && (
                      <p className="text-xs text-zinc-400 mt-1 italic">{payment.paid_comment}</p>
                    )}
                    <p className="text-[10px] text-green-600 mt-0.5">Payment recorded in this month's reports</p>
                  </div>
                )}
              </div>
            )}

            {/* Overdue Warning */}
            {payment.is_overdue && payment.status !== "paid" && (
              <div className="p-3 bg-red-900/20 border border-red-800/30 rounded-lg">
                <p className="text-[10px] text-red-400 uppercase">⚠️ Overdue</p>
                <p className="text-sm text-red-300 mt-1">
                  This payment is overdue. Please process it as soon as possible.
                </p>
              </div>
            )}

            {/* Created Info */}
            <div className="p-3 bg-zinc-800/30 rounded-lg text-xs space-y-1">
              <p className="text-zinc-500">
                Created: <span className="text-zinc-300">{formatDateTime(payment.created_at)}</span>
              </p>
              {payment.updated_at && (
                <p className="text-zinc-500">
                  Updated: <span className="text-zinc-300">{formatDateTime(payment.updated_at)}</span>
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Change History */}
      {auditLog.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <History className="h-4 w-4 text-amber-400" />
              Change History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {auditLog.map((log) => (
                <div key={log.id} className="p-3 bg-zinc-800/50 rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-amber-400 font-medium">{log.user_name}</span>
                    <span className="text-zinc-500 text-xs">{formatDateTime(log.created_at)}</span>
                  </div>
                  <div className="mt-1 text-zinc-300">
                    {log.changes?.map((change, i) => (
                      <span key={i} className="block text-xs">• {change}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-purple-400" />
              Change Payment Status
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="pending" className="text-orange-400">Pending</SelectItem>
                  <SelectItem value="confirmed" className="text-sky-400">Approved</SelectItem>
                  <SelectItem value="paid" className="text-green-400">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newStatus === "paid" && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Payment Date</Label>
                <Input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
                <p className="text-[10px] text-green-500">
                  Payment will appear in reports for this month
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button
                onClick={handleStatusChange}
                disabled={changingStatus}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {changingStatus ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Change Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskPaymentDetailPage;
